// Extensible NOUA Journeys data. To add a new destination later,
// append another object to this array — routes and cards read from it.
// Schema:
// { slug, destination, cardImage, cardTitle, cardSubtitle,
//   openingLine, supportingText, heroImage,
//   title, subtitle, duration,
//   ideaParagraphs: [string],
//   days: [{ num, name, lede, sections: [{ heading?, body: [string], note? }], personalisation? }]
//   closingLine? }

const IMG = (id) => `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=1600&q=85`;

export const JOURNEYS = [
  {
    slug: "paris",
    destination: "PARIS",
    cardImage: IMG("1550340499-a6c60fc8287c"),
    cardTitle: "Paris, Slowly",
    cardSubtitle: "The city, without the checklist.",
    openingLine: "Paris is not a checklist.",
    supportingText:
      "It is a morning that begins slowly, a table you linger at, a gallery you hadn't planned to enter, and a street you turn down simply because it looks interesting.",
    heroImage: IMG("1502602898657-3e91760cbb34"),
    title: "Paris, Slowly",
    subtitle: "The city, without the checklist.",
    duration: "6 DAYS · 5 NIGHTS",
    ideaParagraphs: [
      "Paris doesn't need to be conquered.",
      "For this journey, NOUA takes a different approach: fewer checklists, longer mornings, beautiful places, and enough room for the unexpected.",
      "A stay in a characterful Left Bank hotel becomes the base. Days move between galleries, neighbourhoods, long lunches and small discoveries — with the city's icons appearing naturally rather than becoming a list to tick off.",
      "The pace: considered, but never over-planned.",
    ],
    days: [
      {
        num: "01",
        name: "ARRIVE",
        lede: "Paris, without rushing into it.",
        sections: [
          { body: ["Arrive in Paris and transfer privately to the hotel.", "No sightseeing today.", "Settle into the room. Walk the neighbourhood. Have a drink somewhere close by. Let the city introduce itself."] },
          { heading: "Evening", body: ["A relaxed dinner within walking distance of the hotel."], note: "The first evening of a journey should never feel like an obligation." },
        ],
      },
      {
        num: "02",
        name: "THE LEFT BANK",
        lede: "A neighbourhood, slowly discovered.",
        sections: [
          { body: ["Start slowly with breakfast at a neighbourhood café.", "Walk through Saint-Germain-des-Prés, allowing time for bookshops, galleries and the small streets between destinations.", "Continue towards the Luxembourg Gardens."] },
          { heading: "Afternoon", body: ["Lunch at a carefully selected Left Bank restaurant.", "Later, choose one cultural experience based on the traveller's interests: Musée d'Orsay, Musée de l'Orangerie, or a smaller private gallery experience."] },
          { heading: "Evening", body: ["Nothing scheduled.", "Return to the hotel, dress for dinner and wander out when you're ready."] },
        ],
      },
      {
        num: "03",
        name: "PARIS, YOUR WAY",
        lede: "A day shaped around what you love.",
        sections: [
          { body: ["This day is intentionally personalised."] },
        ],
        personalisation: {
          options: [
            { title: "FOR THE CURIOUS TRAVELLER", text: "A private architecture or design walk through lesser-known Paris." },
            { title: "FOR THE ART LOVER", text: "A morning with a specialist art guide followed by a gallery lunch." },
            { title: "FOR THE FOOD LOVER", text: "A market visit followed by a private cooking experience." },
            { title: "FOR THE FASHION-MINDED", text: "A curated exploration of Parisian maisons, vintage boutiques and independent designers." },
          ],
          closing: "Because sometimes the best part of Paris is whatever you happen to find. The afternoon should remain deliberately open.",
        },
      },
      {
        num: "04",
        name: "THE ICONS, WITHOUT THE CHECKLIST",
        lede: "Yes, you should see the icons. But how you see them matters.",
        sections: [
          { body: ["An early start allows time at the Louvre before the day becomes crowded, followed by a walk through the Tuileries.", "Lunch nearby.", "Then a slower afternoon."] },
          { body: ["As evening approaches, take to the Seine for a private or intimate boat experience. See Paris from the water as the light changes."] },
          { heading: "Dinner", body: ["A table chosen for the evening rather than simply because it is famous."] },
        ],
      },
      {
        num: "05",
        name: "A LITTLE FURTHER",
        lede: "Montmartre, before the crowds arrive.",
        sections: [
          { body: ["Begin early.", "Walk through Montmartre before the crowds arrive, focusing on its streets, studios and small details rather than simply heading towards the Sacré-Cœur.", "Then leave the hill behind.", "Lunch in a neighbourhood where Parisians actually linger."] },
          { body: ["The afternoon is yours.", "Possible options: hotel spa, shopping, another museum, a long café afternoon, or returning to a place discovered earlier in the trip."] },
          { heading: "Final evening", body: ["A beautiful dinner.", "No rushing afterwards.", "Just Paris at night."] },
        ],
      },
      {
        num: "06",
        name: "UNTIL NEXT TIME",
        lede: "A quiet farewell.",
        sections: [
          { body: ["Breakfast.", "One final walk.", "Perhaps a bakery discovered on the first morning.", "Private transfer to the airport or station."] },
        ],
      },
    ],
    closingLine: "A good journey doesn't end when you leave. It leaves something behind.",
  },

  {
    slug: "rajasthan",
    destination: "RAJASTHAN",
    cardImage: IMG("1477587458883-47145ed94245"),
    cardTitle: "Rajasthan, In Its Own Time",
    cardSubtitle: "Where the journey is as important as the destination.",
    openingLine: "There is a certain kind of royalty that belongs only to India.",
    supportingText:
      "In Rajasthan, it lives in the arches of old havelis, the colours of the markets, the quiet of palace courtyards and the rituals of an afternoon that asks you to slow down.",
    heroImage: IMG("1477587458883-47145ed94245"),
    title: "Rajasthan, In Its Own Time",
    subtitle: "Where the journey is as important as the destination.",
    duration: "9 DAYS · 8 NIGHTS",
    ideaParagraphs: [
      "India rewards those who leave room for it.",
      "This journey moves through Delhi → Jaipur → Jodhpur → Udaipur.",
      "But the purpose is not to collect cities. It is to experience them — architecture, food, craft, hospitality, colour and the quieter moments between them.",
      "The journey should feel immersive rather than hurried.",
    ],
    days: [
      { num: "01", name: "DELHI · ARRIVE", lede: "A gentle first evening in the capital.",
        sections: [
          { body: ["Arrive in Delhi and transfer privately to a heritage hotel in the leafy diplomatic quarter.", "No sightseeing today.", "A slow evening walk, a considered dinner, an early night."] },
          { heading: "NOUA note", body: ["Delhi asks patience of you. The best introduction is a quiet one."] },
        ] },
      { num: "02", name: "DELHI · OLD AND NEW", lede: "Two cities in one day.",
        sections: [
          { body: ["A morning wander through Old Delhi with a specialist guide — Chandni Chowk, hidden havelis, small workshops, tea in a place that has been pouring it for generations."] },
          { heading: "Afternoon", body: ["Lunch and rest.", "Later, the calmer contrast of Lutyens' Delhi and the tombs of Humayun and Nizamuddin as the light softens."] },
        ] },
      { num: "03", name: "JAIPUR · THE PINK CITY", lede: "Arrival in the city of colour.",
        sections: [
          { body: ["A private car south into Rajasthan.", "Check in to a beautifully restored haveli or palace hotel.", "The afternoon is left open — a courtyard, a book, tea on a terrace."] },
          { heading: "Evening", body: ["Dinner in a family-run heritage restaurant known to Jaipuris rather than to guidebooks."] },
        ] },
      { num: "04", name: "JAIPUR · CRAFT AND COLOUR", lede: "A day among makers.",
        sections: [
          { body: ["Meet block-printers, jewellers and hand-embroiderers at their studios.", "Learn how a saree becomes what it is."] },
          { heading: "Afternoon", body: ["Lunch in an artist's home.", "A slow visit to the City Palace or Jantar Mantar, chosen for you rather than in a rush."] },
        ] },
      { num: "05", name: "JODHPUR · THE BLUE CITY", lede: "A private drive through the desert.",
        sections: [
          { body: ["Onward to Jodhpur.", "Late-afternoon walk through the old city as the blue houses take on the last of the light."] },
          { heading: "Evening", body: ["Dinner beneath the fort with the city glowing below."] },
        ] },
      { num: "06", name: "JODHPUR · MEHRANGARH", lede: "One fort. All morning.",
        sections: [
          { body: ["A private tour of Mehrangarh with a curator — sound, textile, story.", "Then an unhurried afternoon: a heritage hotel spa, a quiet book, or the bazaar for those who love it."] },
        ] },
      { num: "07", name: "UDAIPUR · THE LAKE CITY", lede: "The most romantic drive in Rajasthan.",
        sections: [
          { body: ["Drive south via Ranakpur, stopping for the marble temple carvings if that appeals.", "Arrive in Udaipur by late afternoon.", "Sunset from a lakeside terrace."] },
        ] },
      { num: "08", name: "UDAIPUR · WATER, LIGHT, STILLNESS", lede: "The quietest day of the journey.",
        sections: [
          { body: ["A private boat on Lake Pichola in the morning light.", "The City Palace slowly, then lunch by the water."] },
          { heading: "Afternoon", body: ["Free. Perhaps a cooking lesson in a Rajasthani home, or nothing at all."] },
          { heading: "Final evening", body: ["A candlelit dinner on a rooftop, palace lights on the water."] },
        ] },
      { num: "09", name: "UNTIL NEXT TIME", lede: "A slow farewell.",
        sections: [
          { body: ["Breakfast on the terrace.", "Private transfer to Udaipur airport."] },
        ] },
    ],
    closingLine: "Rajasthan is not seen quickly. It is lived, briefly.",
  },

  {
    slug: "vietnam",
    destination: "VIETNAM",
    cardImage: IMG("1583417319070-4a69db38a482"),
    cardTitle: "Vietnam, Between the Lines",
    cardSubtitle: "For those who like to look a little closer.",
    openingLine: "Soul food, straw hats and silk.",
    supportingText:
      "Vietnam is a country of beautiful contrasts — where a bowl of pho can be as memorable as a perfectly tailored suit, and where the old and the unexpected meet in the streets of Hoi An.",
    heroImage: IMG("1528127269322-539801943592"),
    title: "Vietnam, Between the Lines",
    subtitle: "For those who like to look a little closer.",
    duration: "9 DAYS · 8 NIGHTS",
    ideaParagraphs: [
      "Some journeys are about seeing more. Others are about noticing more.",
      "This one moves through Hanoi → Ninh Binh → Hoi An.",
      "Food, architecture, local life, landscapes, design, markets, craftsmanship and cultural encounters, with real space to explore independently.",
    ],
    days: [
      { num: "01", name: "HANOI · ARRIVE", lede: "The old capital, gently.",
        sections: [
          { body: ["Arrive in Hanoi and transfer privately to a boutique hotel in the Old Quarter.", "No plans tonight.", "A slow walk. Something warm to eat. Bed."] },
        ] },
      { num: "02", name: "HANOI · FOOD AND LIFE", lede: "A morning of street food, a slower afternoon.",
        sections: [
          { body: ["Meet a food writer for a morning of pho, bun cha and coffee, weaving through the narrow lanes of the Old Quarter."] },
          { heading: "Afternoon", body: ["A quiet visit to the Temple of Literature, then time to wander — or rest."] },
          { heading: "Evening", body: ["A carefully chosen dinner. Water puppetry only if you actually want to."] },
        ] },
      { num: "03", name: "HANOI · ARTS AND CRAFT", lede: "The details of a city.",
        sections: [
          { body: ["Meet a lacquer artist, a silversmith, or a print maker in their studio.", "Lunch in a French colonial villa turned restaurant."] },
          { heading: "Afternoon", body: ["Free. A stroll around Hoan Kiem lake, a book in a café, a shop discovered on foot."] },
        ] },
      { num: "04", name: "NINH BINH · INTO THE LANDSCAPE", lede: "The countryside version of Vietnam.",
        sections: [
          { body: ["A private drive south to Ninh Binh.", "Check in to a beautiful boutique property in the karst valleys."] },
          { heading: "Afternoon", body: ["A quiet sampan through Trang An, rowed by a local — no engine, no hurry."] },
        ] },
      { num: "05", name: "NINH BINH · SLOWER STILL", lede: "A day where the countryside sets the pace.",
        sections: [
          { body: ["Cycle between rice paddies with a picnic lunch, stopping at temples and hidden caves that most visitors miss."] },
          { heading: "Evening", body: ["Dinner at the property, watching the light fall on limestone."] },
        ] },
      { num: "06", name: "HOI AN · LANTERNS AND LIGHT", lede: "Arrival in a town that stays with you.",
        sections: [
          { body: ["Fly south and transfer to Hoi An.", "Check in to a small boutique hotel or private villa near the Old Town."] },
          { heading: "Evening", body: ["A first walk through Hoi An as the lanterns come on."] },
        ] },
      { num: "07", name: "HOI AN · CRAFT AND CLOTH", lede: "A morning with a tailor, an afternoon in a market.",
        sections: [
          { body: ["A visit to a favourite tailor for a private fitting — one considered piece, made properly."] },
          { heading: "Afternoon", body: ["The central market, then a Vietnamese cooking class with a local chef in a home garden."] },
        ] },
      { num: "08", name: "HOI AN · THE COAST OR THE COUNTRY", lede: "Your choice.",
        sections: [
          { body: ["A morning on An Bang beach, or a bicycle ride into Tra Que herb village.", "A long, slow lunch either way."] },
          { heading: "Final evening", body: ["A private dinner in a lantern-lit courtyard.", "Nothing to rush to afterwards."] },
        ] },
      { num: "09", name: "UNTIL NEXT TIME", lede: "A quiet closing.",
        sections: [
          { body: ["Breakfast in the courtyard.", "Private transfer to Da Nang airport."] },
        ] },
    ],
    closingLine: "Vietnam gives you back the pleasure of paying attention.",
  },
];

export const findJourney = (slug) => JOURNEYS.find((j) => j.slug === slug);
